import React, { useEffect, useState, useCallback } from "react";
import Select, { SingleValue } from "react-select";
import { getReq } from "../../utils/request";
import { OrganizationAsset } from "../../types/Organization";
import RatingBadge from "../Rating/RatingBadge";
import classes from "./organizationPicker.module.scss";

interface AssetOption {
    value: OrganizationAsset;
    label: string;
}

interface AssetPickerProps {
    value: OrganizationAsset | null;
    onChange: (asset: OrganizationAsset | null) => void;
    /** Owner organization — required to scope the asset list. */
    organizationId: number;
    /** When true (default), shows rating badge + sorts by rating descending. */
    showRating?: boolean;
    placeholder?: string;
    disabled?: boolean;
    isClearable?: boolean;
    required?: boolean;
    id?: string;
}

/**
 * Shared asset picker — same UX patterns as OrganizationPicker / ContactPicker.
 * Fetches the assets owned by `organizationId` once and lets the user pick one.
 * Each option shows the asset reference number, type/model, and rating badge.
 */
const AssetPicker: React.FC<AssetPickerProps> = ({
    value,
    onChange,
    organizationId,
    showRating = true,
    placeholder = "Select asset...",
    disabled = false,
    isClearable = true,
    required = false,
    id,
}) => {
    const [options, setOptions] = useState<AssetOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadAssets = useCallback(async () => {
        if (!organizationId) {
            setOptions([]);
            return;
        }
        setIsLoading(true);
        try {
            const response = await getReq(`providers/${organizationId}/assets`);
            const list: OrganizationAsset[] =
                response.status === 200
                    ? (response.body?.data ?? response.body ?? [])
                    : [];
            const sorted = [...list];
            if (showRating) {
                sorted.sort((a, b) => {
                    const ar = Number((a as any).averageRating ?? -1);
                    const br = Number((b as any).averageRating ?? -1);
                    if (ar !== br) return br - ar;
                    return (a.referenceNumber || "").localeCompare(b.referenceNumber || "");
                });
            } else {
                sorted.sort((a, b) =>
                    (a.referenceNumber || "").localeCompare(b.referenceNumber || ""),
                );
            }
            setOptions(
                sorted.map((a) => ({
                    value: a,
                    label: a.referenceNumber || `Asset #${a.assetId}`,
                })),
            );
        } finally {
            setIsLoading(false);
        }
    }, [organizationId, showRating]);

    useEffect(() => {
        loadAssets();
    }, [loadAssets]);

    const selectedOption: AssetOption | null = value
        ? { value, label: value.referenceNumber || `Asset #${value.assetId}` }
        : null;

    const formatOptionLabel = (option: AssetOption, meta: { context: string }) => {
        const a = option.value;
        if (meta.context === "menu") {
            const subtitle = [
                a.manufacturer?.name,
                a.model,
                a.bodyStyle,
            ].filter(Boolean).join(" · ");
            return (
                <div className={classes.optionRow}>
                    <div className={classes.optionMain}>
                        <span className={classes.optionName}>{a.referenceNumber}</span>
                        {subtitle && (
                            <span className={classes.optionRef}>{subtitle}</span>
                        )}
                    </div>
                    {showRating && (
                        <RatingBadge
                            average={(a as any).averageRating ?? null}
                            total={(a as any).totalRatings ?? 0}
                            compact
                        />
                    )}
                </div>
            );
        }
        return <span>{option.label}</span>;
    };

    return (
        <div className={classes.pickerWrapper}>
            <div className={classes.pickerControl}>
                <Select<AssetOption>
                    aria-label="Select an asset"
                    inputId={id}
                    value={selectedOption}
                    options={options}
                    isLoading={isLoading}
                    placeholder={placeholder}
                    onChange={(opt: SingleValue<AssetOption>) => onChange(opt?.value ?? null)}
                    noOptionsMessage={() => "No assets found for this provider"}
                    formatOptionLabel={formatOptionLabel}
                    className="custom-select"
                    classNamePrefix="custom-select"
                    isDisabled={disabled || !organizationId}
                    isClearable={isClearable}
                    required={required}
                />
            </div>
            {value && showRating && (
                <RatingBadge
                    average={(value as any).averageRating ?? null}
                    total={(value as any).totalRatings ?? 0}
                    compact
                />
            )}
        </div>
    );
};

export default AssetPicker;
